import { useEffect, useState, useCallback } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { apiGetMySalary, type MySalaryData, type MySalaryPayslip, type PayrollAdjustment } from "@/lib/api";
import { formatMKD, RATES } from "@/lib/mk-salary";
import {
  DollarSign, Download, FileText, TrendingUp, TrendingDown,
  Banknote, Shield, ChevronDown, Calendar, Sparkles, Clock,
  ArrowUpRight, ArrowDownRight, AlertTriangle, Gift, PhoneCall,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { PDFDocument, StandardFonts, rgb, PageSizes } from "pdf-lib";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const adjTypeConfig: Record<string, { icon: typeof Gift; color: string; label: string }> = {
  bonus: { icon: Gift, color: "text-muted-foreground", label: "Bonus" },
  deduction: { icon: TrendingDown, color: "text-muted-foreground", label: "Deduction" },
  on_call: { icon: PhoneCall, color: "text-muted-foreground", label: "On-Call" },
  admin_prohibition: { icon: AlertTriangle, color: "text-muted-foreground", label: "Адм. Забрана" },
  other: { icon: FileText, color: "text-muted-foreground", label: "Other" },
};

const MySalary = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<MySalaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedPayslip, setExpandedPayslip] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiGetMySalary();
      setData(result);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full animate-pulse">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-3 gap-4">{[1,2,3].map((i) => <div key={i} className="h-28 bg-muted rounded-xl" />)}</div>
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  const latest = data?.payslips[0] || null;
  const previous = data?.payslips[1] || null;
  const netDiff = latest && previous ? latest.finalNetSalary - previous.finalNetSalary : 0;
  const netPctChange = previous && previous.finalNetSalary > 0
    ? ((netDiff / previous.finalNetSalary) * 100).toFixed(1) : null;

  const chartData = (data?.payslips || []).slice().reverse().map((p) => ({
    label: `${MONTH_SHORT[p.month - 1]} ${p.year}`,
    gross: p.grossSalary + p.totalAdjustmentsGross,
    net: p.finalNetSalary,
    contributions: p.totalContributions,
    tax: p.personalIncomeTax,
  }));

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div className="animate-in fade-in slide-in-from-left-5 duration-500 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
            <DollarSign className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("salary.title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("salary.subtitle")}</p>
          </div>
        </div>

        {latest && (
          <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 sm:divide-x divide-border/60 divide-y sm:divide-y-0">
              <div className="flex items-center gap-3.5 px-5 py-4">
                <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Banknote className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-xs font-medium text-muted-foreground">{t("salary.lastNet")}</span>
                  <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{formatMKD(latest.finalNetSalary)}</p>
                  {netPctChange && (
                    <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
                      {netDiff >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {netPctChange}% {t("salary.vsLast")}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3.5 px-5 py-4">
                <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-xs font-medium text-muted-foreground">{t("salary.lastGross")}</span>
                  <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{formatMKD(latest.grossSalary + latest.totalAdjustmentsGross)}</p>
                  <span className="text-xs text-muted-foreground">MKD</span>
                </div>
              </div>
              <div className="flex items-center gap-3.5 px-5 py-4">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Shield className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-xs font-medium text-muted-foreground">{t("salary.lastDeductions")}</span>
                  <p className="text-2xl font-bold tabular-nums text-foreground leading-none">{formatMKD(latest.totalDeductions)}</p>
                  <span className="text-xs text-muted-foreground">MKD</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!latest && !loading && (
          <Card className="shadow-card">
            <CardContent className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center animate-pulse">
                <Sparkles className="h-10 w-10 text-primary" />
              </div>
              <p className="font-medium text-foreground">{t("salary.noPayslips")}</p>
              <p className="text-sm text-muted-foreground max-w-md text-center">{t("salary.noPayslipsDesc")}</p>
            </CardContent>
          </Card>
        )}

        {latest && (
          <Tabs defaultValue="payslips" className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
            <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
              <TabsTrigger value="payslips" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                <FileText className="h-3.5 w-3.5" />
                {t("salary.payslipsTab")}
              </TabsTrigger>

              <TabsTrigger value="history" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                <Clock className="h-3.5 w-3.5" />
                {t("salary.historyTab")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="payslips" className="mt-6 space-y-3">
              {(data?.payslips || []).map((slip, idx) => (
                <PayslipCard
                  key={slip.id}
                  slip={slip}
                  index={idx}
                  expanded={expandedPayslip === slip.id}
                  onToggle={() => setExpandedPayslip(expandedPayslip === slip.id ? null : slip.id)}
                  t={t}
                />
              ))}
            </TabsContent>

            <TabsContent value="history" className="mt-6 space-y-6">
              {chartData.length > 1 && (
                <Card className="shadow-card animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />{t("salary.salaryTrend")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="grossGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                        <Tooltip
                          formatter={(value: number) => [`${formatMKD(value)} MKD`, ""]}
                          contentStyle={{ borderRadius: "8px", border: "1px solid hsl(var(--border))", backgroundColor: "hsl(var(--card))", fontSize: 12 }}
                        />
                        <Area type="monotone" dataKey="gross" stroke="#3b82f6" fill="url(#grossGrad)" strokeWidth={2} animationDuration={1200} name={t("salary.gross")} />
                        <Area type="monotone" dataKey="net" stroke="#10b981" fill="url(#netGrad)" strokeWidth={2} animationDuration={1200} name={t("salary.net")} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

            </TabsContent>
          </Tabs>
        )}
      </div>
    </AppLayout>
  );
};

function PayslipCard({ slip, index, expanded, onToggle, t }: {
  slip: MySalaryPayslip; index: number; expanded: boolean; onToggle: () => void; t: any;
}) {
  const hasAdj = slip.adjustments.length > 0;
  const adjustedGross = slip.grossSalary + slip.totalAdjustmentsGross;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await generatePayslipPDF(slip, t);
    } catch (err) {
      console.error("PDF GENERATION ERROR:", err);
      alert("Failed to generate PDF. Check console for details.");
    }
  };

  return (
    <Card
      className="shadow-card hover:shadow-lg transition-all duration-300 cursor-pointer animate-in fade-in slide-in-from-bottom-3 duration-400 group"
      style={{ animationDelay: `${Math.min(index * 40, 300)}ms` }}
      onClick={onToggle}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{MONTH_FULL[slip.month - 1]} {slip.year}</h3>
              <Badge variant="secondary" className="text-[10px] font-normal capitalize">
                {slip.runStatus}
              </Badge>
              {hasAdj && (
                <Badge variant="outline" className="text-[10px] font-normal">
                  {slip.adjustments.length} adj.
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("salary.gross")}: {formatMKD(adjustedGross)} MKD
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-foreground tabular-nums">{formatMKD(slip.finalNetSalary)}</p>
            <p className="text-[10px] text-muted-foreground">{t("salary.net")} MKD</p>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={handleDownload}>
            <Download className="h-4 w-4" />
          </Button>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-300 shrink-0 ${expanded ? "rotate-180" : ""}`} />
        </div>

        <div className={`overflow-hidden transition-all duration-300 ${expanded ? "max-h-[800px] opacity-100 mt-4" : "max-h-0 opacity-0"}`}>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                <BreakdownRow label={t("salary.baseGross")} value={slip.grossSalary} bold />
                {slip.totalAdjustmentsGross !== 0 && (
                  <BreakdownRow label={t("salary.grossAdjustments")} value={slip.totalAdjustmentsGross}
                    highlight={slip.totalAdjustmentsGross > 0 ? "green" : "red"} />
                )}
                {slip.totalAdjustmentsGross !== 0 && (
                  <BreakdownRow label={t("salary.adjustedGross")} value={adjustedGross} bold />
                )}
                <BreakdownRow label={`${t("salary.pension")} (${(RATES.pension * 100).toFixed(1)}%)`} value={slip.pensionContribution} />
                <BreakdownRow label={`${t("salary.health")} (${(RATES.health * 100).toFixed(1)}%)`} value={slip.healthContribution} />
                <BreakdownRow label={`${t("salary.unemployment")} (${(RATES.unemployment * 100).toFixed(1)}%)`} value={slip.unemploymentContribution} />
                <BreakdownRow label={`${t("salary.sickness")} (${(RATES.sickness * 100).toFixed(1)}%)`} value={slip.sicknessContribution} />
                <BreakdownRow label={t("salary.totalContributions")} value={slip.totalContributions} bold />
                <BreakdownRow label={t("salary.grossMinusContrib")} value={slip.grossMinusContributions} />
                <BreakdownRow label={t("salary.personalExemption")} value={slip.personalExemption} />
                <BreakdownRow label={t("salary.taxBase")} value={slip.taxBase} />
                <BreakdownRow label={`${t("salary.incomeTax")} (10%)`} value={slip.personalIncomeTax} />
                <BreakdownRow label={t("salary.totalDeductions")} value={slip.totalDeductions} bold />
                <BreakdownRow label={t("salary.calculatedNet")} value={slip.netSalary} bold />

                {slip.adjustments.filter((a) => a.applyOn === "net").length > 0 && (
                  <>
                    <tr className="bg-muted/50"><td colSpan={3} className="px-4 py-2 text-xs font-semibold text-muted-foreground">{t("salary.netAdjustments")}</td></tr>
                    {slip.adjustments.filter((a) => a.applyOn === "net").map((adj) => {
                      const cfg = adjTypeConfig[adj.type] || adjTypeConfig.other;
                      const AdjIcon = cfg.icon;
                      const sign = (adj.type === "bonus" || adj.type === "on_call") ? 1 : -1;
                      return (
                        <tr key={adj.id} className="border-b">
                          <td className="px-4 py-2 text-sm flex items-center gap-2">
                            <AdjIcon className={`h-3.5 w-3.5 ${cfg.color}`} />
                            <span>{adj.name}</span>
                            <Badge variant="outline" className="text-[9px] ml-1">{cfg.label}</Badge>
                          </td>
                          <td className={`px-4 py-2 text-right tabular-nums text-sm ${sign > 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {sign > 0 ? "+" : "-"}{formatMKD(adj.amount)}
                          </td>
                          <td className="px-2 py-2 text-right text-xs text-muted-foreground">MKD</td>
                        </tr>
                      );
                    })}
                  </>
                )}

                <BreakdownRow label={t("salary.finalNet")} value={slip.finalNetSalary} bold primary />
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownRow({ label, value, bold, primary, highlight }: {
  label: string; value: number; bold?: boolean; primary?: boolean; highlight?: "green" | "red";
}) {
  let cls = "";
  if (primary) cls = "bg-primary/5 text-primary font-bold";
  else if (bold) cls = "font-semibold";

  let valCls = "";
  if (primary) valCls = "text-primary font-bold text-base";
  else if (highlight === "green") valCls = "text-emerald-600";
  else if (highlight === "red") valCls = "text-red-600";
  else if (bold) valCls = "font-semibold";

  return (
    <tr className={`border-b last:border-0 ${cls}`}>
      <td className={`px-4 py-2.5 ${cls}`}>{label}</td>
      <td className={`px-4 py-2.5 text-right tabular-nums w-32 ${valCls}`}>
        {highlight === "green" && value > 0 ? "+" : ""}{formatMKD(value)}
      </td>
      <td className="px-2 py-2.5 text-right text-xs text-muted-foreground w-10">MKD</td>
    </tr>
  );
}

function LatestBreakdown({ slip, t }: { slip: MySalaryPayslip; t: any }) {
  const adjustedGross = slip.grossSalary + slip.totalAdjustmentsGross;
  const sections = [
    {
      title: t("salary.earnings"),
      icon: TrendingUp,
      color: "text-muted-foreground",
      items: [
        { label: t("salary.baseGross"), value: slip.grossSalary },
        ...(slip.adjustments || [])
          .filter((a) => (a.type === "bonus" || a.type === "on_call") && a.applyOn === "gross")
          .map((a) => ({ label: `${a.name} (${adjTypeConfig[a.type]?.label})`, value: a.amount })),
        ...(slip.totalAdjustmentsGross > 0 ? [{ label: t("salary.adjustedGross"), value: adjustedGross }] : []),
      ],
    },
    {
      title: t("salary.contributions"),
      icon: Shield,
      color: "text-muted-foreground",
      items: [
        { label: `${t("salary.pension")} (${(RATES.pension * 100).toFixed(1)}%)`, value: slip.pensionContribution },
        { label: `${t("salary.health")} (${(RATES.health * 100).toFixed(1)}%)`, value: slip.healthContribution },
        { label: `${t("salary.unemployment")} (${(RATES.unemployment * 100).toFixed(1)}%)`, value: slip.unemploymentContribution },
        { label: `${t("salary.sickness")} (${(RATES.sickness * 100).toFixed(1)}%)`, value: slip.sicknessContribution },
      ],
    },
    {
      title: t("salary.taxSection"),
      icon: FileText,
      color: "text-muted-foreground",
      items: [
        { label: t("salary.personalExemption"), value: slip.personalExemption },
        { label: t("salary.taxBase"), value: slip.taxBase },
        { label: `${t("salary.incomeTax")} (10%)`, value: slip.personalIncomeTax },
      ],
    },
  ];

  const netAdj = (slip.adjustments || []).filter((a) => a.applyOn === "net");
  if (netAdj.length > 0) {
    sections.push({
      title: t("salary.netAdjustments"),
      icon: AlertTriangle,
      color: "text-muted-foreground",
      items: netAdj.map((a) => {
        const sign = (a.type === "bonus" || a.type === "on_call") ? 1 : -1;
        return { label: `${a.name} (${adjTypeConfig[a.type]?.label})`, value: sign * a.amount };
      }),
    });
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-card animate-in fade-in slide-in-from-bottom-4 duration-500">
        <CardHeader>
          <CardTitle className="text-base">{MONTH_FULL[slip.month - 1]} {slip.year} — {t("salary.breakdownTab")}</CardTitle>
          <CardDescription>{t("salary.breakdownDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {sections.map((section, si) => {
            const SIcon = section.icon;
            return (
              <div key={si} className="animate-in fade-in slide-in-from-bottom-3 duration-400" style={{ animationDelay: `${si * 80}ms` }}>
                <div className="flex items-center gap-2 mb-3">
                  <SIcon className={`h-4 w-4 ${section.color}`} />
                  <h3 className="text-sm font-semibold">{section.title}</h3>
                </div>
                <div className="space-y-2">
                  {section.items.map((item, ii) => (
                    <div key={ii} className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-muted/50 transition-colors">
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                      <span className="text-sm font-medium tabular-nums">{formatMKD(item.value)} MKD</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="border-t pt-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold text-foreground">{t("salary.finalNet")}</span>
              </div>
              <span className="text-xl font-bold text-foreground tabular-nums">{formatMKD(slip.finalNetSalary)} MKD</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

async function generatePayslipPDF(slip: MySalaryPayslip, t: any) {

  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage(PageSizes.A4);
  const { width, height } = page.getSize();

  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const adjustedGross = slip.grossSalary + slip.totalAdjustmentsGross;
  const monthName = MONTH_FULL[slip.month - 1];

  const primary = rgb(0.22, 0.34, 0.78);  // brand indigo
  const light   = rgb(0.95, 0.96, 1.0);
  const dark    = rgb(0.1, 0.1, 0.1);
  const muted   = rgb(0.5, 0.5, 0.5);
  const divider = rgb(0.85, 0.85, 0.88);

  const marginX = 50;
  const colRight = width - marginX;

  // ── Header band ──────────────────────────────────────────────
  page.drawRectangle({ x: 0, y: height - 80, width, height: 80, color: primary });
  page.drawText("BestHR", { x: marginX, y: height - 38, size: 22, font: bold, color: rgb(1,1,1) });
  page.drawText("PAY SLIP", { x: marginX, y: height - 60, size: 11, font: regular, color: rgb(0.8,0.85,1) });

  const period = `${monthName} ${slip.year}`;
  const periodW = bold.widthOfTextAtSize(period, 16);
  page.drawText(period, { x: colRight - periodW, y: height - 44, size: 16, font: bold, color: rgb(1,1,1) });
  page.drawText(slip.runStatus, {
    x: colRight - regular.widthOfTextAtSize(slip.runStatus, 10),
    y: height - 62, size: 10, font: regular, color: rgb(0.8,0.85,1)
  });

  // ── Helper: draw one breakdown row ───────────────────────────
  let rowY = height - 112;  // start Y

  function drawSection(title: string) {
    page.drawRectangle({ x: marginX, y: rowY - 4, width: colRight - marginX, height: 20, color: light });
    page.drawText(title.toUpperCase(), { x: marginX + 6, y: rowY, size: 8, font: bold, color: primary });
    rowY -= 26;
  }

  function drawRow(label: string, amount: number, opts?: { bold?: boolean; primary?: boolean; positive?: boolean; negative?: boolean }) {
    const isBold = opts?.bold;
    const isStr = opts?.primary;
    const font = (isBold || isStr) ? bold : regular;
    const size = isStr ? 11 : 9;

    if (isStr) {
      page.drawLine({ start: { x: marginX, y: rowY + 10 }, end: { x: colRight, y: rowY + 10 }, thickness: 0.5, color: divider });
    }

    page.drawText(label, { x: marginX + 6, y: rowY, size, font, color: isStr ? primary : dark });

    const fmtAmt = formatMKD(Math.abs(amount)) + " MKD";
    const prefix = opts?.positive ? "+" : opts?.negative || amount < 0 ? "-" : "";
    const amtText = prefix + fmtAmt;
    const amtColor = opts?.positive ? rgb(0,0.55,0.35) : opts?.negative || amount < 0 ? rgb(0.75,0.1,0.1) : isStr ? primary : dark;
    const amtW = font.widthOfTextAtSize(amtText, size);
    page.drawText(amtText, { x: colRight - amtW, y: rowY, size, font, color: amtColor });

    if (isStr) {
      rowY -= 16;
    } else {
      page.drawLine({ start: { x: marginX, y: rowY - 5 }, end: { x: colRight, y: rowY - 5 }, thickness: 0.3, color: divider });
      rowY -= 18;
    }
  }

  // ── Gross earnings ───────────────────────────────────────────
  drawSection("Gross Earnings");
  drawRow("Base Gross Salary", slip.grossSalary);
  if (slip.totalAdjustmentsGross !== 0) {
    drawRow("Gross Adjustments", slip.totalAdjustmentsGross, { positive: slip.totalAdjustmentsGross > 0, negative: slip.totalAdjustmentsGross < 0 });
    drawRow("Adjusted Gross", adjustedGross, { bold: true });
  }

  // ── Deductions ───────────────────────────────────────────────
  rowY -= 8;
  drawSection("Deductions");
  drawRow(`Pension (${(RATES.pension * 100).toFixed(1)}%)`, slip.pensionContribution);
  drawRow(`Health Insurance (${(RATES.health * 100).toFixed(1)}%)`, slip.healthContribution);
  drawRow(`Unemployment (${(RATES.unemployment * 100).toFixed(1)}%)`, slip.unemploymentContribution);
  drawRow(`Sickness Insurance (${(RATES.sickness * 100).toFixed(1)}%)`, slip.sicknessContribution);
  drawRow("Total Contributions", slip.totalContributions, { bold: true });
  drawRow("Gross - Contributions", slip.grossMinusContributions);
  drawRow("Personal Exemption", slip.personalExemption);
  drawRow("Tax Base", slip.taxBase);
  drawRow("Income Tax (10%)", slip.personalIncomeTax);
  drawRow("Total Deductions", slip.totalDeductions, { bold: true });
  drawRow("Calculated Net", slip.netSalary, { bold: true });

  // ── Net adjustments ──────────────────────────────────────────
  const netAdj = slip.adjustments.filter((a) => a.applyOn === "net");
  if (netAdj.length > 0) {
    rowY -= 8;
    drawSection("Net Adjustments");
    for (const a of netAdj) {
      const sign = (a.type === "bonus" || a.type === "on_call") ? 1 : -1;
      drawRow(a.name, sign * a.amount, { positive: sign > 0, negative: sign < 0 });
    }
  }

  // ── Final net ────────────────────────────────────────────────
  rowY -= 12;
  page.drawRectangle({ x: marginX, y: rowY - 8, width: colRight - marginX, height: 30, color: primary });
  rowY += 6;
  page.drawText("FINAL NET SALARY", { x: marginX + 8, y: rowY, size: 11, font: bold, color: rgb(1,1,1) });
  const finalText = formatMKD(slip.finalNetSalary) + " MKD";
  const finalW = bold.widthOfTextAtSize(finalText, 13);
  page.drawText(finalText, { x: colRight - finalW - 8, y: rowY, size: 13, font: bold, color: rgb(1,1,1) });

  // ── Footer ───────────────────────────────────────────────────
  page.drawText("Generated by BestHR  •  Confidential", {
    x: marginX, y: 24, size: 8, font: regular, color: muted,
  });
  const dateStr = new Date().toLocaleDateString("en-GB");
  const dateW = regular.widthOfTextAtSize(dateStr, 8);
  page.drawText(dateStr, { x: colRight - dateW, y: 24, size: 8, font: regular, color: muted });

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payslip-${slip.year}-${String(slip.month).padStart(2, "0")}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default MySalary;
